import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { APIProvider, useMapsLibrary } from '@vis.gl/react-google-maps';

const GMAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY || '';

/**
 * "Can we take this one?"
 *
 * 2026-08-30: "Check if inquiry is located in our operating zone, if its close
 * to any or its between two locations ask a manager in case we can onboard."
 *
 * Type a building and it answers from the same boundaries the map draws:
 *
 * Two answers, green or amber (2026-08-30):
 *
 *   GREEN  — within the zone. Take it.
 *   AMBER  — not in the zone. If it is near one, or sitting between two, it
 *            says to confirm with a manager rather than refusing it.
 *
 * Amber and not red even when it is plainly far away: this is never a refusal,
 * only a reading. Four tiers and a paragraph of reasoning were here first and
 * they were more than anybody needs to glance at.
 */

/** Wraps the box in its own API provider, so it works anywhere on the page. */
export default function ZoneCheck({ onPick }) {
    if (!GMAPS_KEY) return null;
    return (
        <APIProvider apiKey={GMAPS_KEY} libraries={['places']}>
            <ZoneCheckBox onPick={onPick} />
        </APIProvider>
    );
}

function ZoneCheckBox({ onPick }) {
    const placesLib = useMapsLibrary('places');
    const hostRef = useRef(null);
    const elRef = useRef(null);
    const [result, setResult] = useState(null);

    const assess = useCallback(async (pt, label) => {
        setResult({ label, pending: true });
        try {
            const { data } = await axios.post('/api/leads/zone-check', pt);
            setResult({ label, ...data });
            onPick?.({ ...pt, label });
        } catch (e) {
            setResult({ label, error: e.response?.data?.error || 'Could not check that address.' });
        }
    }, [onPick]);

    useEffect(() => {
        if (!placesLib || !hostRef.current || elRef.current) return undefined;
        const host = hostRef.current;
        const el = new window.google.maps.places.PlaceAutocompleteElement();
        try { el.includedRegionCodes = ['ae']; } catch { /* older SDKs */ }
        el.placeholder = 'Is this address in our zone? Search a building…';
        el.style.width = '100%';
        el.style.height = '38px';
        el.style.border = '1px solid #d1d5db';
        el.style.borderRadius = '8px';
        el.style.background = '#fff';

        const onSelect = async (evt) => {
            const pp = evt?.placePrediction;
            if (!pp) return;
            const place = pp.toPlace();
            await place.fetchFields({ fields: ['location', 'displayName'] });
            const loc = place.location;
            if (!loc) return;
            assess({ lat: loc.lat(), lng: loc.lng() }, place.displayName || 'That address');
        };

        el.addEventListener('gmp-select', onSelect);
        host.appendChild(el);
        elRef.current = el;
        return () => {
            try {
                el.removeEventListener('gmp-select', onSelect);
                if (host.contains(el)) host.removeChild(el);
            } catch { /* already detached */ }
            elRef.current = null;
        };
    }, [placesLib, assess]);

    // Always km to one decimal — "2.0 km" reads as a distance, "1756 m" reads
    // as a measurement.
    const km = (m) => `${(m / 1000).toFixed(1)} km`;

    return (
        <div className="lds__zone">
            <div ref={hostRef} />
            {result?.pending && <div className="lds__zoneMaybe"><strong>Checking…</strong></div>}
            {result?.error && <div className="lds__err">{result.error}</div>}
            {result && !result.pending && !result.error && (
                result.inside.length ? (
                    <div className="lds__zoneOk">
                        <strong>Within the zone — {result.inside.join(' / ')}.</strong>
                    </div>
                ) : (
                    <div className="lds__zoneMaybe">
                        <strong>
                            Not in the zone - In case near to a zone or between two zones
                            ask a manager to onboard
                        </strong>
                        <span>
                            {(result.near.length ? result.near : [result.nearest])
                                .filter(Boolean)
                                .map((n) => `${n.area} is ${km(n.metres)} away`)
                                .join(' · ')}
                        </span>
                    </div>
                )
            )}
        </div>
    );
}
